import { useEffect, useMemo, useRef, useState } from 'react';
import type { CharacterSummary } from '../types';
import { classColor, factionColor, isStale } from '../lib/wow';

interface Props {
  chars: CharacterSummary[];
  selectedKey: string | null;
  onSelect: (key: string) => void;
  onAddNew?: () => void;

  /** Compare-mode controls. */
  compareMode: boolean;
  compareKeys: string[];                          // up to 2
  onToggleCompareMode: () => void;
  onToggleCompareKey: (key: string) => void;

  /** "Go to dashboard" link in the popover. */
  onOpenDashboard?: () => void;

  /** Optional explicit ref to programmatically focus the search input. */
  searchRef?: React.RefObject<HTMLInputElement>;
}

interface Group {
  owner: string;        // display label
  ownerKey: string;     // raw owner value or ""
  chars: CharacterSummary[];
}

const OTHER_LABEL = 'Legacy / shared';

function groupByOwner(list: CharacterSummary[]): Group[] {
  const map = new Map<string, Group>();
  for (const c of list) {
    const key = c.owner ?? '';
    const label = c.owner ?? OTHER_LABEL;
    let g = map.get(key);
    if (!g) { g = { owner: label, ownerKey: key, chars: [] }; map.set(key, g); }
    g.chars.push(c);
  }
  for (const g of map.values()) {
    g.chars.sort((a, b) => (b.level - a.level) || a.name.localeCompare(b.name));
  }
  return Array.from(map.values()).sort((a, b) => {
    if (!a.ownerKey && b.ownerKey) return 1;
    if (a.ownerKey && !b.ownerKey) return -1;
    if (b.chars.length !== a.chars.length) return b.chars.length - a.chars.length;
    return a.owner.localeCompare(b.owner);
  });
}

/** Flat ordering used by the global [ / ] cycle shortcuts. */
export function flatPickerOrder(list: CharacterSummary[]): CharacterSummary[] {
  return groupByOwner(list).flatMap((g) => g.chars);
}

export function CharacterPicker({
  chars,
  selectedKey,
  onSelect,
  onAddNew,
  compareMode,
  compareKeys,
  onToggleCompareMode,
  onToggleCompareKey,
  onOpenDashboard,
  searchRef,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const rootRef = useRef<HTMLDivElement>(null);
  const internalSearchRef = useRef<HTMLInputElement>(null);
  const inputRef = searchRef ?? internalSearchRef;

  const current = chars.find((c) => c.key === selectedKey);
  const currentColor = current ? classColor(current.classFile) : undefined;

  // Auto-expand the owner of the currently-selected character whenever
  // the popover opens, so users see context for where they are.
  useEffect(() => {
    if (!open) return;
    const ownerKey = current?.owner ?? '';
    setExpanded((prev) => {
      if (prev.has(ownerKey) || (ownerKey === '' && prev.has(''))) return prev;
      const next = new Set(prev);
      next.add(ownerKey);
      return next;
    });
  }, [open, current?.owner]);

  // Close on outside click / Esc.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open, inputRef]);

  const q = query.trim().toLowerCase();
  const isSearching = q.length > 0;

  const groups = useMemo(() => {
    if (!isSearching) return groupByOwner(chars);
    return groupByOwner(chars).map((g) => {
      const minLvl = /^>=?\s*(\d+)$/.exec(q);
      const filt = g.chars.filter((c) => {
        if (minLvl) return c.level >= Number(minLvl[1]);
        return (
          c.name.toLowerCase().includes(q) ||
          c.realm.toLowerCase().includes(q) ||
          c.classFile.toLowerCase().includes(q) ||
          (c.owner ?? '').toLowerCase().includes(q)
        );
      });
      return { ...g, chars: filt };
    }).filter((g) => g.chars.length > 0);
  }, [chars, q, isSearching]);

  function isExpanded(g: Group): boolean {
    // While searching, auto-expand every group that has matches so the
    // user can see the results without an extra click.
    if (isSearching) return true;
    return expanded.has(g.ownerKey);
  }

  function toggleGroup(g: Group) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(g.ownerKey)) next.delete(g.ownerKey);
      else next.add(g.ownerKey);
      return next;
    });
  }

  function handleRowClick(c: CharacterSummary) {
    if (compareMode) {
      onToggleCompareKey(c.key);
      return;
    }
    onSelect(c.key);
    setOpen(false);
  }

  return (
    <div className="char-picker" ref={rootRef}>
      <button
        type="button"
        className="char-picker-trigger"
        onClick={() => setOpen((v) => !v)}
        style={currentColor ? { borderColor: currentColor + '66', color: currentColor } : undefined}
        aria-expanded={open}
      >
        <span className="char-picker-trigger-label">Character</span>
        <span className="char-picker-trigger-name">
          {current
            ? `${current.name} · L${current.level} ${pretty(current.classFile)}`
            : 'Pick a character'}
        </span>
        <span className="char-picker-trigger-caret">▾</span>
      </button>

      {open && (
        <div className="char-picker-popover" role="dialog" aria-label="Pick a character">
          <div className="char-picker-toolbar">
            <input
              ref={inputRef}
              className="char-picker-search"
              placeholder="Search any account or character…   (>=70 for level)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <label className="char-picker-compare-toggle" title="Pick 2 characters to compare side-by-side (shortcut: c)">
              <input
                type="checkbox"
                checked={compareMode}
                onChange={onToggleCompareMode}
              />
              Compare
            </label>
          </div>

          <div className="char-picker-body">
            {groups.length === 0 && (
              <div className="char-picker-empty">No matches.</div>
            )}
            {groups.map((g) => {
              const exp = isExpanded(g);
              return (
                <section key={g.ownerKey || '__other__'} className="char-picker-group">
                  <button
                    type="button"
                    className={['char-picker-group-header', exp ? 'is-open' : ''].join(' ').trim()}
                    onClick={() => toggleGroup(g)}
                    aria-expanded={exp}
                    title={g.ownerKey ? `Account: ${g.owner}` : 'Characters with no known owner (legacy/shared token)'}
                  >
                    <span className="char-picker-group-caret">{exp ? '▾' : '▸'}</span>
                    <span className="char-picker-group-name">{g.owner}</span>
                    <span className="char-picker-group-count">{g.chars.length}</span>
                  </button>
                  {exp && (
                    <ul className="char-picker-list">
                      {g.chars.map((c) => {
                        const color = classColor(c.classFile);
                        const fc = factionColor(c.faction);
                        const stale = isStale(c.updatedAt);
                        const selected = c.key === selectedKey && !compareMode;
                        const checked = compareKeys.includes(c.key);
                        return (
                          <li
                            key={c.key}
                            className={[
                              'char-picker-row',
                              selected ? 'is-selected' : '',
                              checked ? 'is-compare-on' : '',
                              stale ? 'is-stale' : '',
                            ].join(' ').trim()}
                            onClick={() => handleRowClick(c)}
                            style={{ borderLeftColor: fc }}
                          >
                            {compareMode && (
                              <input
                                type="checkbox"
                                className="char-picker-row-check"
                                checked={checked}
                                onChange={(e) => { e.stopPropagation(); onToggleCompareKey(c.key); }}
                                onClick={(e) => e.stopPropagation()}
                                disabled={!checked && compareKeys.length >= 2}
                              />
                            )}
                            <div className="char-picker-row-main">
                              <div className="char-picker-row-name" style={{ color }}>
                                {c.name}
                              </div>
                              <div className="char-picker-row-meta">
                                <span className="lvl">L{c.level}</span>
                                <span className="cls" style={{ color }}>{pretty(c.classFile)}</span>
                                <span className="realm">· {c.realm}</span>
                                {stale && <span className="chip stale" title="Not updated in 14+ days">stale</span>}
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </section>
              );
            })}
          </div>

          <div className="char-picker-footer">
            {onOpenDashboard && (
              <button
                type="button"
                className="char-picker-add"
                onClick={() => { setOpen(false); onOpenDashboard(); }}
                title="Back to the dashboard"
              >
                ⌂ Dashboard
              </button>
            )}
            {onAddNew && (
              <button
                type="button"
                className="char-picker-add"
                onClick={() => { setOpen(false); onAddNew(); }}
              >
                + Add a new character…
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function pretty(classFile: string): string {
  return classFile.charAt(0) + classFile.slice(1).toLowerCase();
}
